import { BriefcaseBusiness, Command, Search, UserRound, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'
import { useAppStore } from '../../store/AppStore'

type CommandItem = { label: string; hint: string; href: string; icon: LucideIcon; image?: string }

const staticLinks: CommandItem[] = [
  { label: 'Découvrir les athlètes', hint: 'Marketplace', href: '/athletes', icon: UserRound },
  { label: 'Voir les opportunités', hint: 'Briefs actifs', href: '/opportunites', icon: BriefcaseBusiness },
  { label: 'Ouvrir les messages', hint: 'Conversations', href: '/app/messages', icon: Command },
]

export function CommandPalette({ buttonClass = '' }: { buttonClass?: string }) {
  const { state } = useAppStore()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setOpen((value) => !value)
      }
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const results = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const athletes: CommandItem[] = state.athletes.map((athlete) => ({
      label: `${athlete.firstName} ${athlete.lastName}`,
      hint: `${athlete.discipline} · ${athlete.city}`,
      href: `/athletes/${athlete.slug}`,
      image: athlete.photo,
      icon: UserRound,
    }))
    const briefs: CommandItem[] = state.briefs.filter((brief) => brief.status === 'active').map((brief) => ({
      label: brief.title,
      hint: `${brief.territory} · ${brief.budget.toLocaleString('fr-FR')} €`,
      href: `/opportunites#${brief.id}`,
      icon: BriefcaseBusiness,
    }))
    return [...staticLinks, ...athletes, ...briefs].filter((item) => !needle || `${item.label} ${item.hint}`.toLowerCase().includes(needle)).slice(0, 9)
  }, [query, state.athletes, state.briefs])

  const go = (href: string) => {
    setOpen(false)
    setQuery('')
    navigate(href)
  }

  return <>
    <button className={`command-trigger ${buttonClass}`} onClick={() => setOpen(true)} aria-label="Ouvrir la recherche rapide">
      <Search size={16} /><span>Rechercher</span><kbd>⌘ K</kbd>
    </button>
    {open && <div className="command-layer" role="dialog" aria-modal="true" aria-label="Recherche rapide" onMouseDown={(event) => { if (event.target === event.currentTarget) setOpen(false) }}>
      <section className="command-palette">
        <header><Search size={20} /><input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Athlète, sport, ville ou opportunité…" /><button onClick={() => setOpen(false)} aria-label="Fermer"><X /></button></header>
        <div className="command-results">
          <span className="command-label">Résultats</span>
          {results.map((item) => {
            const Icon = item.icon
            return <button onClick={() => go(item.href)} key={`${item.href}-${item.label}`}>
              {item.image ? <img src={item.image} alt="" /> : <i><Icon size={17} /></i>}
              <span><strong>{item.label}</strong><small>{item.hint}</small></span>
              <em>↵</em>
            </button>
          })}
          {results.length === 0 && <div className="command-empty">Aucun résultat. Essayez un sport ou une ville.</div>}
        </div>
        <footer><span><kbd>↑</kbd><kbd>↓</kbd> naviguer</span><span><kbd>esc</kbd> fermer</span></footer>
      </section>
    </div>}
  </>
}
